import { MetaLocationJob } from '@/constants/meta-job';
import type {
  JobFilterOption,
  JobFilterKey,
  JobFilters,
  JobFilterState,
  JobMultiFilterKey,
  JobSingleFilterKey,
  JobSearchFilters,
  JobSearchRequest,
} from './model';

export const JOB_SEARCH_ENDPOINT = '/external/api/v1/opportunities/search';
export const SEO_ALL_JOBS_ENDPOINT = '/external/api/v1/meta/seo-all-jobs';
export const HOT_JOB_KEYWORDS_ENDPOINT =
  '/external/api/v1/opportunities/hot-keywords';
export const JOB_SEARCH_SUGGESTIONS_ENDPOINT =
  '/opportunity-search/public/v1/suggest';
export const JOB_DETAIL_ENDPOINT = '/external/api/v1/job';
export const SIMILAR_JOBS_ENDPOINT =
  '/external/api/v1/opportunities/similar-job';
export const JOB_SALARY_MARKET_ENDPOINT =
  '/external/api/v1/opportunities/job-salary-market';
export const SAVED_JOB_ENDPOINT = '/external/api/v1/opportunities/saved-job';
export const UNSAVED_JOB_ENDPOINT =
  '/external/api/v1/opportunities/unsaved-job';
export const LIST_SAVED_JOB_ENDPOINT =
  '/external/api/v1/opportunities/list-saved-job';
export const SAVED_JOBS_PAGE_SIZE = 20;
export const SIMILAR_JOBS_LIMIT = 6;
export const JOB_REQUEST_TIMEOUT_MS = 15_000;

export const JOBS_FIRST_PAGE = 1;
export const JOBS_PAGE_SIZE = 50;
export const JOBS_DESKTOP_INITIAL_PAGE_SIZE = 16;
export const JOBS_MOBILE_PAGE_SIZE = 8;
export const JOBS_MAX_PAGE = 1_000;
export const JOBS_MAX_PAGE_SIZE = 50;
export const JOBS_MAX_QUERY_LENGTH = 200;
export const JOBS_MAX_ADDRESS_LENGTH = 100;
export const JOB_SEARCH_QUERY_PARAM = 'q';
export const JOB_PAGE_QUERY_PARAM = 'page';

export const ALL_JOB_FILTERS = 'all';
export const JOB_MULTI_FILTER_QUERY_KEYS = [
  'location',
  'salaryType',
  'jobLevel',
  'employmentType',
  'workingArrangement',
  'workingOnWeekend',
] as const satisfies ReadonlyArray<JobMultiFilterKey>;
export const JOB_SINGLE_FILTER_QUERY_KEYS = [
  'postedWithin',
  'numOfApplications',
] as const satisfies ReadonlyArray<JobSingleFilterKey>;
export const JOB_FILTER_QUERY_KEYS = [
  ...JOB_MULTI_FILTER_QUERY_KEYS,
  ...JOB_SINGLE_FILTER_QUERY_KEYS,
] as const satisfies ReadonlyArray<JobFilterKey>;
export const JOB_SEARCH_QUERY_KEYS = JOB_FILTER_QUERY_KEYS;
export const DEFAULT_JOB_FILTERS: JobFilterState = {
  location: [],
  salaryType: [],
  jobLevel: [],
  postedWithin: ALL_JOB_FILTERS,
  employmentType: [],
  workingArrangement: [],
  workingOnWeekend: [],
  numOfApplications: ALL_JOB_FILTERS,
};

const JOB_FILTER_VALUE_PATTERN = /^[a-z0-9_-]{1,100}$/i;

function getFilterId(value?: string): string | number | undefined {
  if (!value || value === ALL_JOB_FILTERS) return undefined;

  const numericValue = Number(value);

  return Number.isNaN(numericValue) ? value : numericValue;
}

function getSelectedFilterOptions(
  options?: JobFilterOption[]
): JobFilterOption[] {
  return options?.filter(option => option.isSelected) ?? [];
}

function buildSelectedFilterOptions(
  values: string[] | undefined,
  options?: JobFilterOption[]
): JobFilterOption[] {
  const selectedIds = new Set(
    (values ?? [])
      .map(getFilterId)
      .filter((id): id is string | number => id !== undefined)
      .map(String)
  );

  if (options?.length) {
    return options.map(option => ({
      ...option,
      isSelected: selectedIds.has(String(option.id)),
    }));
  }

  return (values ?? []).flatMap(value => {
    const id = getFilterId(value);

    if (id === undefined) return [];

    return [
      {
        id,
        value: String(id),
        count: 0,
        isSelected: true,
      },
    ];
  });
}

function buildSelectedFilterOption(
  value: string | undefined,
  options?: JobFilterOption[]
): JobFilterOption[] {
  return buildSelectedFilterOptions(
    value && value !== ALL_JOB_FILTERS ? [value] : [],
    options
  );
}

function getValidFilterValues(
  rawValue: string | string[] | undefined
): string[] {
  const values = (Array.isArray(rawValue) ? rawValue : [rawValue]).flatMap(
    value => value?.split(',').map(item => item.trim()) ?? []
  );

  return values.filter((value): value is string =>
    Boolean(
      value && value !== ALL_JOB_FILTERS && JOB_FILTER_VALUE_PATTERN.test(value)
    )
  );
}

export function parseJobFilterSearchParams(
  searchParams: Record<string, string | string[] | undefined>
): Partial<JobFilterState> {
  const filters: Partial<JobFilterState> = {};

  for (const key of JOB_MULTI_FILTER_QUERY_KEYS) {
    const values = getValidFilterValues(searchParams[key]);

    if (values.length > 0) filters[key] = [...new Set(values)];
  }

  for (const key of JOB_SINGLE_FILTER_QUERY_KEYS) {
    const value = getValidFilterValues(searchParams[key])[0];

    if (value) filters[key] = value;
  }

  return filters;
}

export function buildJobSearchPayload(
  query: string,
  filters: Partial<JobFilterState>,
  page: number,
  filterOptions: Partial<JobFilters> = {},
  limit = JOBS_PAGE_SIZE
): JobSearchRequest & {
  page: number;
  limit: number;
  query: string;
  sort: string;
  filter: JobSearchFilters;
} {
  const requestFilters: JobSearchFilters = {
    postedWithin: buildSelectedFilterOption(
      filters.postedWithin,
      filterOptions.postedWithin
    ),
    type: getSelectedFilterOptions(filterOptions.type),
    salaryType: buildSelectedFilterOptions(
      filters.salaryType,
      filterOptions.salaryType
    ),
    jobLevel: buildSelectedFilterOptions(
      filters.jobLevel,
      filterOptions.jobLevel
    ),
    industry: [],
    workingArrangement: buildSelectedFilterOptions(
      filters.workingArrangement,
      filterOptions.workingArrangement
    ),
    employmentType: buildSelectedFilterOptions(
      filters.employmentType,
      filterOptions.employmentType
    ),
    location: buildSelectedFilterOptions(
      filters.location,
      filterOptions.location
    ),
    numOfApplications: buildSelectedFilterOption(
      filters.numOfApplications,
      filterOptions.numOfApplications
    ),
    workingOnWeekend: buildSelectedFilterOptions(
      filters.workingOnWeekend,
      filterOptions.workingOnWeekend
    ),
  };

  return {
    sort: '',
    page,
    filter: requestFilters,
    limit,
    query: query.trim().slice(0, JOBS_MAX_QUERY_LENGTH),
  };
}

export const DEFAULT_JOB_SEARCH_REQUEST = buildJobSearchPayload(
  '',
  {},
  JOBS_FIRST_PAGE
);

const UUID_JOB_ID_SOURCE =
  '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const LEGACY_NUMERIC_JOB_ID_SOURCE = '[1-9][0-9]{5,}';
const JOB_ID_SOURCE = `(?:${UUID_JOB_ID_SOURCE}|${LEGACY_NUMERIC_JOB_ID_SOURCE})`;
const JOB_ID_PATTERN = new RegExp(`^${JOB_ID_SOURCE}$`, 'i');
const JOB_DETAIL_SEGMENT_PATTERN = new RegExp(
  `^(.+?)-(${JOB_ID_SOURCE})$`,
  'i'
);
const LOCATION_ROUTE_PREFIX = 'tai-';
const LOCATION_SLUGS = new Set(
  MetaLocationJob.map(location => slugifyJobText(location.value))
);
const JOBS_LOCALE = 'vi-VN';
const JOBS_TIME_ZONE = 'Asia/Ho_Chi_Minh';

export function isValidJobId(jobId: string): boolean {
  return JOB_ID_PATTERN.test(jobId);
}

export function slugifyJobText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function getCanonicalJobSearchRedirectPath(
  segment: string
): string | null {
  let decodedSegment = segment;

  try {
    decodedSegment = decodeURIComponent(segment);
  } catch {
    // Keep the original segment so malformed URL encoding can still be
    // normalized instead of failing the request.
  }

  if (parseJobDetailSegment(decodedSegment)) return null;

  const canonicalSegment = slugifyJobText(decodedSegment);

  if (!canonicalSegment || canonicalSegment === segment) return null;

  return `/viec-lam/${canonicalSegment}`;
}

export function getJobSearchPath(
  query: string,
  address: string,
  filters: JobFilterState,
  page = JOBS_FIRST_PAGE
): string {
  const searchParams = new URLSearchParams();
  const normalizedQuery = query.trim().slice(0, JOBS_MAX_QUERY_LENGTH);
  const querySlug = slugifyJobText(normalizedQuery);
  const primaryAddress = address.split(',')[0]?.trim() ?? '';
  const addressSlug = slugifyJobText(primaryAddress);
  const combinedQuery = [
    querySlug,
    addressSlug ? `${LOCATION_ROUTE_PREFIX}${addressSlug}` : '',
  ]
    .filter(Boolean)
    .join('-');

  if (combinedQuery) searchParams.set(JOB_SEARCH_QUERY_PARAM, combinedQuery);

  for (const key of JOB_MULTI_FILTER_QUERY_KEYS) {
    const values = filters[key];

    if (values.length > 0) searchParams.set(key, values.join(','));
  }

  for (const key of JOB_SINGLE_FILTER_QUERY_KEYS) {
    const value = filters[key];

    if (value !== ALL_JOB_FILTERS) searchParams.set(key, value);
  }

  const normalizedPage = parseJobSearchPage(String(page));

  if (normalizedPage > JOBS_FIRST_PAGE) {
    searchParams.set(JOB_PAGE_QUERY_PARAM, String(normalizedPage));
  }

  const queryString = searchParams.toString().replaceAll('%2C', ',');

  return queryString ? `/viec-lam?${queryString}` : '/viec-lam';
}

export function parseJobSearchPage(
  value: string | string[] | undefined
): number {
  const rawValue = Array.isArray(value) ? value[0] : value;
  const page = Number(rawValue);

  if (!Number.isInteger(page) || page < JOBS_FIRST_PAGE) {
    return JOBS_FIRST_PAGE;
  }

  return Math.min(page, JOBS_MAX_PAGE);
}

export function parseJobSearchQuery(query: string): {
  addressSlug: string | null;
  keyword: string;
} {
  const normalizedQuery = query.trim().slice(0, JOBS_MAX_QUERY_LENGTH);
  const locationSlug = getLocationSlug(normalizedQuery);

  if (locationSlug) {
    return {
      addressSlug: locationSlug,
      keyword: '',
    };
  }

  const addressSeparator = ` ${LOCATION_ROUTE_PREFIX}`;
  const separatorIndex = normalizedQuery.lastIndexOf(addressSeparator);

  if (separatorIndex >= 0) {
    const addressSlug = normalizedQuery.slice(
      separatorIndex + addressSeparator.length
    );

    if (!LOCATION_SLUGS.has(addressSlug)) {
      return parseJobSearchSegment(normalizedQuery);
    }

    return {
      addressSlug,
      keyword: normalizedQuery.slice(0, separatorIndex).trim(),
    };
  }

  return parseJobSearchSegment(normalizedQuery);
}

export function getJobDetailPath(job: {
  alias?: string | null;
  id: string;
  jobTitle: string;
}): string {
  const alias = job.alias?.trim();

  if (alias) return `/viec-lam/${encodeURIComponent(alias)}`;

  const slug = slugifyJobText(job.jobTitle) || 'viec-lam';

  return `/viec-lam/${slug}-${job.id}`;
}

export function parseJobDetailSegment(
  segment: string
): { slug: string; jobId: string } | null {
  const match = segment.match(JOB_DETAIL_SEGMENT_PATTERN);

  if (!match) return null;

  return { slug: match[1], jobId: match[2] };
}

export function getLocationSlug(segment: string): string | null {
  if (!segment.startsWith(LOCATION_ROUTE_PREFIX)) return null;

  const locationSlug = segment.slice(LOCATION_ROUTE_PREFIX.length);

  return LOCATION_SLUGS.has(locationSlug) ? locationSlug : null;
}

export function parseJobSearchSegment(segment: string): {
  addressSlug: string | null;
  keyword: string;
} {
  const locationSlug = getLocationSlug(segment);

  if (locationSlug) return { addressSlug: locationSlug, keyword: '' };

  const addressSeparator = `-${LOCATION_ROUTE_PREFIX}`;
  const separatorIndex = segment.lastIndexOf(addressSeparator);

  if (separatorIndex <= 0) {
    return {
      addressSlug: null,
      keyword: getKeywordFromSegment(segment),
    };
  }

  const addressSlug = segment.slice(separatorIndex + addressSeparator.length);

  if (!LOCATION_SLUGS.has(addressSlug)) {
    return {
      addressSlug: null,
      keyword: getKeywordFromSegment(segment),
    };
  }

  return {
    addressSlug,
    keyword: getKeywordFromSegment(segment.slice(0, separatorIndex)),
  };
}

export function getKeywordFromSegment(segment: string): string {
  return segment.replace(/-+/g, ' ').trim();
}

export function formatJobsUpdatedAt(date: Date): string {
  return new Intl.DateTimeFormat(JOBS_LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: JOBS_TIME_ZONE,
  }).format(date);
}
